import { useEffect, useRef, useState } from "react";
import type {
  ConversationMessage,
  EmailContent,
  ItemCommsMessage,
} from "@forgepoint/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmailViewer } from "@/components/activityEvents/EmailViewer";
import { MarkdownReact } from "@/components/MarkdownReact";
import { AttachmentPill } from "@/components/AttachmentPill";
import { AssetPreviewDialog } from "@/components/AssetPreviewDialog";
import { cn } from "@/lib/utils";
import { useItemCommsMessagesStore } from "./itemCommsMessagesStore";
import { commsChannelForProvider } from "./commsChannel";
import { formatCommsTimestamp } from "./commsMessageFormat";

/**
 * Opens a referenced comms message in the full context of its conversation. The
 * conversation is a single rail, so email renders through {@link EmailViewer}
 * and every other rail through a plain message thread. The referenced message
 * is highlighted and scrolled into view.
 * @param message - The referenced message whose conversation to show, or null when closed
 * @param onClose - Called when the modal is dismissed
 */
export const CommsConversationModal = ({
  message,
  onClose,
}: {
  message: ItemCommsMessage | null;
  onClose: () => void;
}) => {
  const { conversationsById, conversationLoadingById, fetchConversation } =
    useItemCommsMessagesStore();
  const conversationId = message?.conversationId ?? null;
  const messages = conversationId
    ? conversationsById[conversationId]
    : undefined;
  const loading = conversationId
    ? conversationLoadingById[conversationId]
    : undefined;

  useEffect(() => {
    if (conversationId) {
      fetchConversation(conversationId).catch(() => undefined);
    }
  }, [conversationId, fetchConversation]);

  const isEmail = message
    ? commsChannelForProvider(message.provider) === "email"
    : false;
  const [previewAttachment, setPreviewAttachment] = useState<{
    assetId: string;
    fileName: string;
  } | null>(null);

  return (
    <>
      <Dialog
        open={message !== null}
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <DialogContent className="flex max-h-[80vh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b px-5 py-4 text-left">
            <DialogTitle className="truncate text-base">
              {message?.subject || "Conversation"}
            </DialogTitle>
            <DialogDescription>
              The full conversation this cited message belongs to.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {loading?.isLoading && !messages ? <ConversationSkeleton /> : null}
            {loading?.error && !messages ? (
              <ConversationError
                message={loading.error}
                onRetry={() =>
                  conversationId
                    ? fetchConversation(conversationId).catch(() => undefined)
                    : undefined
                }
              />
            ) : null}
            {messages ? (
              <ConversationThread
                messages={messages}
                highlightedId={message?.commsMessageId ?? null}
                isEmail={isEmail}
                onOpenAttachment={setPreviewAttachment}
              />
            ) : null}
            {!conversationId && message ? (
              <>
                <ConversationThread
                  messages={[toStandaloneMessage(message)]}
                  highlightedId={message.commsMessageId}
                  isEmail={isEmail}
                  onOpenAttachment={setPreviewAttachment}
                />
                <p className="mt-3 text-xs italic text-muted-foreground">
                  The full conversation isn&apos;t available for this message.
                </p>
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
      <AssetPreviewDialog
        assetId={previewAttachment?.assetId ?? null}
        fileName={previewAttachment?.fileName}
        onClose={() => setPreviewAttachment(null)}
      />
    </>
  );
};

const ConversationThread = ({
  messages,
  highlightedId,
  isEmail,
  onOpenAttachment,
}: {
  messages: ConversationMessage[];
  highlightedId: string | null;
  isEmail: boolean;
  onOpenAttachment: (attachment: { assetId: string; fileName: string }) => void;
}) => {
  const highlightRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    highlightRef.current?.scrollIntoView({ block: "center" });
  }, [messages, highlightedId]);

  return (
    <div className="flex flex-col gap-3">
      {messages.map((conversationMessage) => {
        const highlighted = conversationMessage.id === highlightedId;
        return (
          <div
            key={conversationMessage.id}
            ref={highlighted ? highlightRef : undefined}
            className={cn(
              "rounded-lg",
              highlighted && "ring-2 ring-primary/40",
            )}
          >
            {isEmail ? (
              <EmailMessage message={conversationMessage} />
            ) : (
              <BubbleMessage message={conversationMessage} />
            )}
            <MessageAttachments
              attachments={conversationMessage.attachments}
              onOpen={onOpenAttachment}
            />
          </div>
        );
      })}
    </div>
  );
};

/** Attachment chips under a message; clicking one previews the asset. */
const MessageAttachments = ({
  attachments,
  onOpen,
}: {
  attachments: ConversationMessage["attachments"];
  onOpen: (attachment: { assetId: string; fileName: string }) => void;
}) => {
  if (attachments.length === 0) {
    return null;
  }
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {attachments.map((attachment) => (
        <AttachmentPill
          key={attachment.assetId}
          fileName={attachment.fileName}
          onClick={() => onOpen(attachment)}
        />
      ))}
    </div>
  );
};

const EmailMessage = ({ message }: { message: ConversationMessage }) => (
  <div className="flex flex-col gap-1">
    <MetaLine message={message} />
    <EmailViewer emailContent={toEmailContent(message)} />
  </div>
);

const BubbleMessage = ({ message }: { message: ConversationMessage }) => (
  <div
    className={cn(
      "rounded-lg border p-3",
      isInbound(message) ? "bg-card" : "bg-muted/40",
    )}
  >
    <div className="mb-1 flex items-center gap-2">
      <span className="truncate text-sm font-medium text-foreground">
        {senderOf(message)}
      </span>
      <span className="text-xs text-muted-foreground">
        {directionLabel(message)}
      </span>
      <span className="ml-auto shrink-0 text-xs text-muted-foreground">
        {formatCommsTimestamp(message.createdAt)}
      </span>
    </div>
    {message.body ? (
      <div className="prose prose-sm dark:prose-invert max-w-none break-words text-sm leading-relaxed text-foreground/90 prose-pre:whitespace-pre-wrap prose-pre:break-words">
        <MarkdownReact>{message.body}</MarkdownReact>
      </div>
    ) : (
      <p className="text-sm italic text-muted-foreground">No message body.</p>
    )}
  </div>
);

const MetaLine = ({ message }: { message: ConversationMessage }) => (
  <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
    <span>{directionLabel(message)}</span>
    <span className="ml-auto">{formatCommsTimestamp(message.createdAt)}</span>
  </div>
);

const ConversationSkeleton = () => (
  <div className="flex flex-col gap-3">
    {[0, 1, 2].map((row) => (
      <div key={row} className="flex flex-col gap-2 rounded-lg border p-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    ))}
  </div>
);

const ConversationError = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) => (
  <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
    <p className="text-sm text-muted-foreground">{message}</p>
    <Button variant="outline" size="sm" onClick={onRetry}>
      Retry
    </Button>
  </div>
);

/** Shapes a referenced message for rendering alone when its row has no thread. */
const toStandaloneMessage = (
  message: ItemCommsMessage,
): ConversationMessage => ({
  id: message.commsMessageId,
  direction: message.direction,
  provider: message.provider,
  from: message.from,
  senderLabel: message.senderLabel,
  to: message.to,
  cc: [],
  bcc: [],
  subject: message.subject,
  body: message.body,
  htmlBody: null,
  createdAt: message.createdAt,
  attachments: [],
});

const toEmailContent = (message: ConversationMessage): EmailContent => ({
  from: message.senderLabel ?? message.from ?? undefined,
  to: message.to,
  cc: message.cc,
  bcc: message.bcc,
  subject: message.subject ?? undefined,
  // Bodies arrive markdown-first from the conversation endpoint; the raw
  // htmlBody would be mangled by EmailViewer's markdown renderer.
  body: message.body ?? undefined,
});

const isInbound = (message: ConversationMessage): boolean =>
  message.direction !== "outbound";

const directionLabel = (message: ConversationMessage): string =>
  isInbound(message) ? "Received" : "Sent";

const senderOf = (message: ConversationMessage): string =>
  message.senderLabel ?? message.from ?? "Unknown sender";
